import React, { createContext, useContext, useState, useCallback } from "react";
import useDebounce from "../hooks/useDebounce";

const PaginationContext = createContext();
const FilterSortContext = createContext();
const DataContext = createContext();

export const TableProvider = ({ children }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalDocs, setTotalDocs] = useState(10);

    const [sortBy, setSortBy] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    const refetch = useCallback(async (fetchFn) => {
        if (typeof fetchFn !== "function") return;
        setLoading(true);
        try {
            const res = await fetchFn({
                page: currentPage,
                limit: itemsPerPage,
                sortBy,
                searchTerm: debouncedSearchTerm,
            });
            setData(res?.data);
            setTotalDocs(res?.data?.totalDocs);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [currentPage, itemsPerPage, sortBy, debouncedSearchTerm]);

    return (
        <PaginationContext.Provider value={{ currentPage, setCurrentPage, itemsPerPage, setItemsPerPage, totalDocs, setTotalDocs }}>
            <FilterSortContext.Provider value={{ sortBy, setSortBy, searchTerm, setSearchTerm }}>
                <DataContext.Provider value={{ data, loading, refetch }}>
                    {children}
                </DataContext.Provider>
            </FilterSortContext.Provider>
        </PaginationContext.Provider>
    );
};

export const usePagination = () => {
    const ctx = useContext(PaginationContext);
    if (!ctx) throw new Error("usePagination must be used inside TableProvider");
    return ctx;
};

export const useFilterSort = () => {
    const ctx = useContext(FilterSortContext);
    if (!ctx) throw new Error("useFilterSort must be used inside TableProvider");
    return ctx;
};

export const useTableData = () => {
    const ctx = useContext(DataContext);
    if (!ctx) throw new Error("useTableData must be used inside TableProvider");
    return ctx;
};
